import type { ComponentProps, SyntheticEvent } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface CampoCredencialProps extends Omit<ComponentProps<'input'>, 'type' | 'autoComplete'> {
  /** Esconde o valor na tela, como um campo de senha. */
  mascarado?: boolean;
}

const AVISO_AO_LEITOR_DE_TELA = 'Fica oculto na tela, mas o leitor de tela lê o que for digitado.';

function impedir(evento: SyntheticEvent) {
  evento.preventDefault();
}

/**
 * Campo de credencial de terceiro (Client ID e Client Secret do RTC, senha do certificado), que o
 * gerenciador de senhas do navegador não pode confundir com o login do TurboPro.
 *
 * Por isso nunca é `type="password"`, nem mascarado. O Chrome trata todo campo de senha, com o
 * campo de texto antes dele, como formulário de login e ignora `autocomplete="off"` ali: guardou o
 * par RTC como login do site, que passou a aparecer na tela de entrada, e preenchia o login do
 * usuário nos campos do RTC, a um clique de "Substituir Credenciais" virar a credencial da empresa.
 * Sem campo de senha na página, o gerenciador do Chrome, do Edge, do Firefox e do Safari não monta
 * login nem oferece guardar; a máscara fica com o `-webkit-text-security`, que todos aplicam
 * (o Firefox desde a versão 114).
 *
 * Os `data-*` pedem o mesmo aos gerenciadores de terceiros: 1Password, LastPass (que nem sempre
 * obedece), Bitwarden (só se o usuário ligou a opção de o site excluir campos) e Dashlane. Sem
 * corretor porque o ortográfico aprimorado do Chrome envia o texto ao Google e o teclado do celular
 * capitalizaria o valor.
 *
 * O que se perde em relação ao campo de senha, e atributo nenhum devolve: o leitor de tela lê o
 * valor (daí o aviso em `aria-description`), a previsão de texto do Windows pode aprendê-lo e o Edge
 * não mostra o próprio botão de revelar. Copiar, recortar e arrastar continuam bloqueados.
 */
export function CampoCredencial({ mascarado = false, className, ...props }: CampoCredencialProps) {
  const protecaoDaMascara = mascarado
    ? { onCopy: impedir, onCut: impedir, onDragStart: impedir }
    : {};

  return (
    <Input
      {...props}
      {...protecaoDaMascara}
      type="text"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      data-1p-ignore
      data-lpignore="true"
      data-bwignore
      data-form-type="other"
      aria-description={mascarado ? AVISO_AO_LEITOR_DE_TELA : undefined}
      className={cn(mascarado && '[-webkit-text-security:disc]', className)}
    />
  );
}
